import React from 'react';
import type AssignIssueParentModalType from '@atlassian/jira-assign-issue-parent-modal';
import { AssignIssueParentModalBoundary } from '@atlassian/jira-assign-issue-parent-modal/src/async.tsx';
import type { Issue } from '@atlassian/jira-assign-issue-parent-modal/src/model/types.tsx';
import type { IssueParent } from '@atlassian/jira-issue-parent-services/src/services/types.tsx';
import { lazy } from '@atlassian/react-loosely-lazy';
import { BACKLOG_PACKAGE_NAME } from '../../../common/constants/analytics';
import { AssignIssueParentType } from '../../../model/modals/types';
import { useBacklogActionCreator, useBacklogDispatch, useBacklogSelector } from '../../../state';
import { cardsParentSetOptimistic } from '../../../state/actions/cards/parent';
import { closeModal } from '../../../state/actions/modals';
import {
	getBaseUrl,
	getIsCompanyManaged,
	getProjectIds,
	projectIdSelector,
} from '../../../state/selectors/app';
import { partialCardsFromCardIdsSelector } from '../../../state/selectors/cards';
import { getFirstLevelHierarchyName } from '../../../state/selectors/issue-hierarchy';
import {
	partialIssueParentSelector,
	firstParentIssueTypeNameSelector,
	issueHierarchyLevelSelector,
	issueParentTypeIdsSelector,
} from '../../../state/selectors/issue-parent';
import { getOpenModalData } from '../../../state/selectors/modals';

export type OwnProps = {
	analyticsSource: string;
};

export type Props = OwnProps;

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const AssignIssueParentModal = lazy<typeof AssignIssueParentModalType>(
	() =>
		import(
			/* webpackChunkName: "async-assign-issue-parent-modal" */ '@atlassian/jira-assign-issue-parent-modal'
		),
);

export default function AssignIssueParentModalContainer() {
	const modalData = useBacklogSelector(getOpenModalData);
	const issues = useBacklogSelector((state) =>
		modalData && modalData.type === AssignIssueParentType
			? partialCardsFromCardIdsSelector(state)(modalData.payload.cardIds)
			: [],
	);
	const issueParents = useBacklogSelector(partialIssueParentSelector);
	const issueParentTypeIds = useBacklogSelector(issueParentTypeIdsSelector);
	const issueHierarchyLevel = useBacklogSelector(issueHierarchyLevelSelector)();
	const parentIssueTypeName = useBacklogSelector(firstParentIssueTypeNameSelector);
	const firstLevelHierarchyName = useBacklogSelector(getFirstLevelHierarchyName);
	const baseUrl = useBacklogSelector(getBaseUrl);
	const currentProjectId = useBacklogSelector(projectIdSelector);
	const projectIds = useBacklogSelector(getProjectIds);
	const isCompanyManaged = useBacklogSelector(getIsCompanyManaged);

	const dispatch = useBacklogDispatch();
	const onClose = useBacklogActionCreator(closeModal);

	const label = isCompanyManaged ? firstLevelHierarchyName : parentIssueTypeName;

	const onSubmit = (
		cards: Issue[],
		parentId: string | null,
		issueParent: IssueParent | null,
		requestPromise: Promise<unknown>,
	) => {
		dispatch(
			cardsParentSetOptimistic({
				// @ts-expect-error - TS2322 - Type 'Issue[]' is not assignable to type 'PartialCard[]'.
				cards,
				// @ts-expect-error - TS2322 - Type 'string | null' is not assignable to type 'number | null'.
				parentId,
				requestPromise,
			}),
		);
	};

	return (
		<AssignIssueParentModalBoundary packageName={BACKLOG_PACKAGE_NAME}>
			<AssignIssueParentModal
				issues={issues}
				issueParents={issueParents}
				issueParentTypeIds={issueParentTypeIds}
				issueHierarchyLevel={issueHierarchyLevel}
				parentIssueTypeName={label}
				baseUrl={baseUrl}
				currentProjectId={currentProjectId}
				projectIds={projectIds}
				onClose={onClose}
				onSubmit={onSubmit}
				isSimplifiedProject={!isCompanyManaged}
			/>
		</AssignIssueParentModalBoundary>
	);
}
